import type { ValueSetExpandParams } from '@medplum/core'
import type { SeenMedplumClient } from '@seen/core/client'
import type { ComboboxAsyncOption } from '#src/components/core/combobox/ComboboxAsync.tsx'
import {
  defaultToOption,
  type ValueSetAutocompleteValue,
} from '#src/components/ValueSetAutocomplete/toOptions.ts'

const DEFAULT_COUNT_PER_BINDING = 10

// Loads autocomplete options by expanding one or more ValueSet bindings.
//
// When more than one binding is passed, each is expanded independently with
// its own `countPerBinding` budget and the results are merged. This is what
// lets the allergen picker union RxNorm and SNOMED without one code system
// crowding the other out: a single `$expand` over a composite ValueSet returns
// members in include-array order and stops at `count`, so a high-volume system
// (RxNorm has 100+ "mango"/"soy"/"penicillin" products) can bury the food
// concept the clinician wants. Per-binding budgets guarantee every source is
// sampled; the caller's Fuse layer then ranks the merged set by the query.
export async function valueSetAutocompleteLoadOptions({
  input,
  signal,
  medplum,
  expandParams,
  binding,
  countPerBinding = DEFAULT_COUNT_PER_BINDING,
  toOption = defaultToOption,
}: {
  input: string
  signal: AbortSignal
  medplum: SeenMedplumClient
  expandParams?: Partial<ValueSetExpandParams>
  binding: string | string[]
  countPerBinding?: number
  toOption?: (
    element: ValueSetAutocompleteValue
  ) => ComboboxAsyncOption<ValueSetAutocompleteValue>
}): Promise<ComboboxAsyncOption<ValueSetAutocompleteValue>[]> {
  const bindings = Array.isArray(binding) ? binding : [binding]

  // async callback so a synchronous throw becomes a rejected settle, keeping
  // one failing source from taking down the others.
  const results = await Promise.allSettled(
    bindings.map(async (url) =>
      medplum.valueSetExpand(
        { ...expandParams, url, filter: input, count: countPerBinding },
        { signal }
      )
    )
  )

  // A newer keystroke may have aborted these requests mid-flight; never resolve
  // a stale merged result on top of the in-flight one.
  if (signal.aborted) {
    throw new DOMException('Aborted', 'AbortError')
  }

  const expansions = results.flatMap((result) =>
    result.status === 'fulfilled' ? [result.value] : []
  )

  // Only fail if every binding failed; a single failed source degrades to the
  // remaining ones rather than blanking the picker.
  if (expansions.length === 0) {
    const firstReason = results.find(
      (result): result is PromiseRejectedResult => result.status === 'rejected'
    )?.reason
    throw firstReason ?? new Error('ValueSet expansion failed')
  }

  const seen = new Set<string>()
  const newData: ValueSetAutocompleteValue[] = []
  for (const expansion of expansions) {
    for (const element of expansion.expansion?.contains ?? []) {
      const { code, display, system } = element
      if (!code || !display || !system) {
        continue
      }
      // Dedup by system+code, not code alone: an identical numeric code can
      // exist in two systems, and merged bindings can overlap (Seen's combined
      // set also includes the SNOMED source).
      const key = `${system}|${code}`
      if (seen.has(key)) {
        continue
      }
      seen.add(key)
      newData.push({ code, display, system })
    }
  }

  return newData.map(toOption)
}
